import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/app/admin/_common/ui/card";
import { dayjsTz } from "@/app/_utils/dayjs";
import type { LocationSalesSummary } from "@/app/_services/LocationService";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const compactCurrencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function CustomTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const point = payload[0].payload;
    return (
      <div className="rounded-md border border-border bg-card px-3 py-2 text-[12px] shadow-sm">
        <div className="font-medium text-foreground">
          {dayjsTz(point.date).format("MMM D, YYYY")}
        </div>
        <div className="text-muted-foreground">
          {currencyFormat.format(point.cumulative_revenue)}
        </div>
      </div>
    );
  }
  return null;
}

export default function LocationSalesCard({ sales }: { sales: LocationSalesSummary }) {
  const chartData = sales.series.map((point) => ({
    ...point,
    timestamp: dayjsTz(point.date).valueOf(),
  }));

  const rows = [
    { label: "Sell-through", value: `${sales.sell_through_pct}%` },
    { label: "Tickets sold", value: `${sales.tickets_sold} / ${sales.tickets_available}` },
    { label: "Gross revenue", value: currencyFormat.format(sales.gross_revenue) },
    { label: "Avg. ticket", value: currencyFormat.format(sales.avg_ticket_price) },
    {
      label: "Refunds",
      value: `${sales.refund_count} · ${currencyFormat.format(sales.refund_amount)}`,
    },
  ];

  return (
    <Card className="rounded-md p-4 shadow-none">
      <div className="mb-3 text-[13.5px] font-semibold text-foreground">Sales</div>

      <div className="mb-2">
        <div className="mb-1.5 flex items-center justify-between text-[12.5px]">
          <span className="text-muted-foreground">Sell-through</span>
          <span className="font-medium text-foreground">{sales.sell_through_pct}%</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground"
            style={{ width: `${Math.min(100, sales.sell_through_pct)}%` }}
          />
        </div>
      </div>

      <div className="mt-3">
        {rows.slice(1).map((row) => (
          <div key={row.label} className="flex items-center justify-between py-1.5 text-[12.5px]">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-medium text-foreground">{row.value}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 h-[150px] pt-2">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="locationSalesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--foreground))" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={["dataMin", "dataMax"]}
                tickFormatter={(value) => dayjsTz(value).format("MMM YYYY")}
                stroke="hsl(var(--border))"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                minTickGap={40}
              />
              <YAxis
                tickFormatter={(value) => compactCurrencyFormat.format(value)}
                stroke="hsl(var(--border))"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={44}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulative_revenue"
                stroke="hsl(var(--foreground))"
                strokeWidth={1.5}
                fill="url(#locationSalesGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
            No sales yet
          </div>
        )}
      </div>
      <div className="mt-1 text-center text-[11px] text-muted-foreground/70">
        Cumulative revenue over time
      </div>
    </Card>
  );
}
